import { useState } from 'react'
import toast from 'react-hot-toast'
import { Copy, Dices, CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
} from '@jira-clone/ui'
import { api } from '../api/client'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { useDepartments } from '../shared/hooks/useDepartments'
import { STORABLE_ROLES } from '../shared/hooks/usePermissionTemplates'

interface CreateUserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: () => void
}

const EMPTY_FORM = {
  email: '',
  password: '',
  name: '',
  username: '',
  employeeId: '',
  department: '',
  departmentRole: '',
  role: 'MEMBER' as (typeof STORABLE_ROLES)[number],
}

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$'
  let out = ''
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)]
  return out
}

export function CreateUserModal({ open, onOpenChange, onCreated }: CreateUserModalProps) {
  const { data: departments = [] } = useDepartments()
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null)

  const close = () => {
    onOpenChange(false)
    // Reset after the close animation so the form doesn't visibly flash empty.
    setTimeout(() => {
      setForm(EMPTY_FORM)
      setCreated(null)
    }, 200)
  }

  const handleSubmit = async () => {
    if (!form.email.trim() || !form.password.trim() || !form.department) {
      toast.error('Email, password, and department are required.')
      return
    }
    if (form.password.trim().length < 6) {
      toast.error('Password must be at least 6 characters.')
      return
    }
    setSaving(true)
    try {
      await api.post<any>(
        '/users',
        {
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim() || undefined,
          username: form.username.trim() || undefined,
          employeeId: form.employeeId.trim() || undefined,
          department: form.department,
          departmentRole: form.departmentRole.trim() || undefined,
          role: form.role,
        },
        { skipErrorToast: true }
      )
      setCreated({ email: form.email.trim(), password: form.password })
      onCreated?.()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'create this user'))
    } finally {
      setSaving(false)
    }
  }

  const copyPassword = () => {
    if (!created) return
    navigator.clipboard?.writeText(created.password)
    toast.success('Password copied.')
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                User Created
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-muted-foreground">
                Share these credentials with <strong>{created.email}</strong> — this password won't be shown again.
              </p>
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
                <code className="text-sm font-mono flex-1 truncate">{created.password}</code>
                <Button variant="ghost" size="icon" onClick={copyPassword} title="Copy password">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Create User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label htmlFor="cu-email">Email</Label>
                <Input
                  id="cu-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="name@company.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cu-password">Temporary Password</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="cu-password"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Min 6 characters"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setForm((f) => ({ ...f, password: generatePassword() }))}
                    title="Generate a random password"
                  >
                    <Dices className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cu-name">Name</Label>
                  <Input
                    id="cu-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cu-username">Username</Label>
                  <Input
                    id="cu-username"
                    value={form.username}
                    onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cu-department">Department</Label>
                  <select
                    id="cu-department"
                    value={form.department}
                    onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="">Select a department</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cu-role">Role</Label>
                  <select
                    id="cu-role"
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as (typeof STORABLE_ROLES)[number] }))}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {STORABLE_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r === 'OWNER' ? 'Owner (superadmin)' : r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cu-dept-role">Job Title (optional)</Label>
                  <Input
                    id="cu-dept-role"
                    value={form.departmentRole}
                    onChange={(e) => setForm((f) => ({ ...f, departmentRole: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cu-employee-id">Employee ID (optional)</Label>
                  <Input
                    id="cu-employee-id"
                    value={form.employeeId}
                    onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
